import React from 'react';
import { alpha, makeStyles, Table, TableBody, TableHead, TableRow, Typography, LinearProgress, Box, Chip } from '@material-ui/core';
import { HeadCell } from '../molecules/Table/HeadCell.jsx';
import { BasicTableCell } from '../molecules/Table/BasicTableCell.jsx';
import { MultiAvatar } from '../molecules/MultiAvatar.jsx';
import { palette } from '../../settings/themes/colors.js';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

export const INDICATOR_COLUMN_KEYS = Object.freeze({
  INDICATOR: 'indicator',
  TYPE: 'type',
  VARIABLES: 'variables',
  LINE_BASE: 'lineBase',
  GOAL: 'goal',
  FREQUENCY: 'frequency',
  RESPONSIBLE: 'responsible',
  STATUS: 'status'
});

const COLUMN_META = {
  [INDICATOR_COLUMN_KEYS.INDICATOR]: { intl: 'common.indicator', width: '380px' },
  [INDICATOR_COLUMN_KEYS.TYPE]: { intl: 'common.type', width: '90px' },
  [INDICATOR_COLUMN_KEYS.VARIABLES]: { intl: 'common.variables', width: '90px' },
  [INDICATOR_COLUMN_KEYS.LINE_BASE]: { intl: 'project.indicator.form.lineBase', width: '90px' },
  [INDICATOR_COLUMN_KEYS.GOAL]: { intl: 'project.formulation.form.goal', width: '90px' },
  [INDICATOR_COLUMN_KEYS.FREQUENCY]: { intl: 'project.indicator.table.frequency', width: '90px' },
  [INDICATOR_COLUMN_KEYS.RESPONSIBLE]: { intl: 'common.responsible', width: '90px' },
  [INDICATOR_COLUMN_KEYS.STATUS]: { intl: 'common.status', width: '90px' }
};

const DEFAULT_COLUMN_DEFS = Object.values(INDICATOR_COLUMN_KEYS)
  .filter(key => key !== INDICATOR_COLUMN_KEYS.TYPE)
  .map(key => ({ key }));

const normalizeColumnDefs = columnDefs => {
  if (!columnDefs) return DEFAULT_COLUMN_DEFS;
  return columnDefs.map(col => (typeof col === 'string' ? { key: col } : col));
};

const useStyles = makeStyles({
  containerRow: {
    borderBottom: `1px solid ${palette.darkGray20}`
  },
  chip: {
    borderRadius: 8,
    border: `1px solid ${alpha(palette.dimGray, 0.2)}`,
    color: `${palette.white} !important`,
    backgroundColor: palette.captuDataYellow
  },
  chipNotStart: {
    backgroundColor: palette.darkGray20,
    '& *': {
      color: palette.dimGray
    }
  },
  chipInProcess: {
    backgroundColor: palette.captuDataYellow
  },
  chipCompleted: {
    backgroundColor: palette.emerald
  },
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start'
  },
  progressBarContainer: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    width: '100%',
    flexWrap: 'nowrap'
  },
  progressText: {
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  progressBar: {
    padding: 1,
    height: 8,
    flex: 1,
    background: palette.silver,
    border: 'none',
    borderRadius: 10
  },
  progressBarNotStart: {
    backgroundColor: palette.darkGray20
  },
  progressBarInProcess: {
    backgroundColor: palette.captuDataYellow
  },
  progressBarCompleted: {
    backgroundColor: palette.emerald
  }
});

export const TableIndicator = ({ indicators = [], projectId, taskId, columnDefs: columnDefsProp, disableLink = false }) => {
  const classes = useStyles();
  const columns = normalizeColumnDefs(columnDefsProp);

  const STATUS_PROGRESSES_CLASSNAMES = Object.freeze({
    not_started: classes['progressBarNotStart'],
    in_progress: classes['progressBarInProcess'],
    completed: classes['progressBarCompleted']
  });

  const STATUS_CHIP_CLASSNAMES = Object.freeze({
    not_started: classes['chipNotStart'],
    in_progress: classes['chipInProcess'],
    completed: classes['chipCompleted']
  });

  const cellRenderers = {
    [INDICATOR_COLUMN_KEYS.INDICATOR]: indicator => (
      <BasicTableCell>
        <Typography>
          {disableLink ? (
            indicator.name
          ) : (
            <Link to={`/projects/${projectId}/formulation/${taskId}/indicators/${indicator.id}`}>{indicator.name}</Link>
          )}
        </Typography>
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.TYPE]: indicator => (
      <BasicTableCell>
        <Typography>{indicator.typeName}</Typography>
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.VARIABLES]: indicator => (
      <BasicTableCell>
        {(indicator.indicatorVariables || []).map(({ indicatorVariableType = {} }, idx) => (
          <Typography key={idx}>
            {indicatorVariableType.name}
            {idx < (indicator.indicatorVariables || []).length - 1 ? ', ' : ''}
          </Typography>
        ))}
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.LINE_BASE]: indicator => (
      <BasicTableCell>
        <Typography>{indicator.baseLineHuminized}</Typography>
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.GOAL]: indicator => (
      <BasicTableCell>
        <Typography>{indicator.goalHuminized}</Typography>
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.FREQUENCY]: indicator => (
      <BasicTableCell>
        <Typography>
          <FormattedMessage
            id={`project.indicator.details.frequencyMeasure.${indicator.measurementFrequency}`}
            defaultMessage={indicator.measurementFrequency}
          />
        </Typography>
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.RESPONSIBLE]: indicator => (
      <BasicTableCell>
        <MultiAvatar names={[(indicator.responsible || {}).fullName || '']} />
      </BasicTableCell>
    ),
    [INDICATOR_COLUMN_KEYS.STATUS]: indicator => (
      <BasicTableCell>
        <Box className={classes.statusCell}>
          <Chip label={indicator.progressLabel} className={`${classes.chip} ${STATUS_CHIP_CLASSNAMES[indicator.progressStatus]}`} />
          <Box className={classes.progressBarContainer}>
            <LinearProgress
              value={indicator.progressPercentage}
              variant="determinate"
              classes={{
                root: classes.progressBar,
                bar: STATUS_PROGRESSES_CLASSNAMES[indicator.progressStatus]
              }}
            />
            <Typography className={classes.progressText}>{indicator.progressPercentage} %</Typography>
          </Box>
        </Box>
      </BasicTableCell>
    )
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          {columns.map(({ key }) => {
            const meta = COLUMN_META[key];
            if (!meta) return null;
            return (
              <HeadCell key={key} style={{ width: meta.width }}>
                <FormattedMessage id={meta.intl} />
              </HeadCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {indicators.map((indicator, index) => (
          <TableRow key={index} className={classes.containerRow}>
            {columns.map(({ key }) => {
              const renderer = cellRenderers[key];
              if (!renderer) return null;
              return <React.Fragment key={key}>{renderer(indicator)}</React.Fragment>;
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

TableIndicator.propTypes = {
  indicators: PropTypes.array,
  projectId: PropTypes.number,
  taskId: PropTypes.number,
  columnDefs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ key: PropTypes.string.isRequired })])),
  disableLink: PropTypes.bool
};
